import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react"
import { useStore } from "zustand"
import { useHotkeys } from "react-hotkeys-hook"
import {
  ViewportState, ViewportStore, ZoomOptions, createViewportStore,
} from "./stores/createViewportStore"
import { DocumentState, DocumentStore, createDocumentStore } from "./stores/createDocumentStore"
import { SelectionState, SelectionStore, createSelectionStore } from "./stores/createSelectionStore"
import {
  Keybinding, KeyboardLayout, isControlTarget, toHotkeys, typesKey,
} from "./lib/keybindings"

type EditorContextValue = {
  /** Screen pixels per content unit at 100%. */
  scale: number
  viewport: ViewportStore
  document: DocumentStore<unknown>
  selection: SelectionStore
  keybindings: Record<string, Keybinding>
}

const EditorContext = createContext<EditorContextValue | null>(null)

type Props<T> = {
  zoom: ZoomOptions
  /**
   * Screen pixels per content unit at 100% (8 for a badge in mm). Everything the editor takes and
   * gives, positions, sizes, the selection box, is in content units.
   */
  scale?: number
  /** The document the editor starts from. The editor owns it from then on. */
  defaultValue: T
  /** Called after each change the user makes (edits, undo, redo), never for `defaultValue`. */
  onChange?: (value: T) => void
  /** The editor's shortcuts by name; components listen to them with `useShortcut`. */
  keybindings?: Record<string, Keybinding>
  historyLimit?: number
  children: React.ReactNode
}

/**
 * The root of a canvas editor. It holds the editor's stores (the document with its history,
 * the viewport and the selection), one set per editor, so two editors on a page never share
 * state. To load another document, remount it with a new `key`.
 */
const Editor = <T, >({
  zoom,
  scale = 1,
  defaultValue,
  onChange,
  keybindings = {},
  historyLimit = 50,
  children,
}: Props<T>) => {
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const [stores] = useState(() => ({
    viewport: createViewportStore(zoom, scale),
    document: createDocumentStore(defaultValue, {
      historyLimit,
      onChange: (value) => onChangeRef.current?.(value),
    }) as DocumentStore<unknown>,
    selection: createSelectionStore(),
  }))
  const [value] = useState(() => ({ ...stores, keybindings, scale }))

  return (
    <EditorContext.Provider value={value}>
      {children}
    </EditorContext.Provider>
  )
}

const useEditorContext = () => {
  const context = useContext(EditorContext)
  if (!context) throw new Error("Editor hooks must be used inside an Editor")

  return context
}

/** Reads one slice of the viewport; the component re-renders only when that slice changes. */
export const useViewport = <U, >(selector: (state: ViewportState) => U) => (
  useStore(useEditorContext().viewport, selector)
)

/** Screen pixels per content unit at 100%. */
export const useContentScale = () => useEditorContext().scale

/** The viewport's state at call time, for event handlers, without subscribing. */
export const useGetViewport = () => useEditorContext().viewport.getState

/** The viewport store itself, for the canvas that keeps a view in step with it. */
export const useViewportStore = () => useEditorContext().viewport

export const useZoom = () => useViewport((state) => state.zoom)

/** Reads one slice of the document state; re-renders only when that slice changes. */
export const useEditorDocument = <T, U>(selector: (state: DocumentState<T>) => U) => (
  useStore(useEditorContext().document as unknown as DocumentStore<T>, selector)
)

/** The document as it is now. */
export const useDocument = <T, >() => useEditorDocument<T, T>((state) => state.history.present)

/** The document at call time, for event handlers, without subscribing. */
export const useGetDocument = <T, >() => {
  const store = useEditorContext().document as unknown as DocumentStore<T>

  return useCallback(() => store.getState().history.present, [store])
}

type Change<T> = DocumentState<T>["change"]

export const useChangeDocument = <T, >() => useEditorDocument<T, Change<T>>((state) => state.change)

export const useUndo = () => useEditorDocument((state) => state.undo)
export const useRedo = () => useEditorDocument((state) => state.redo)
export const useCanUndo = () => useEditorDocument((state) => state.history.past.length > 0)
export const useCanRedo = () => useEditorDocument((state) => state.history.future.length > 0)

/** Reads one slice of the selection; re-renders only when that slice changes. */
export const useSelection = <U, >(selector: (state: SelectionState) => U) => (
  useStore(useEditorContext().selection, selector)
)

export const useSelectedIds = () => useSelection((state) => state.selectedIds)

/** The selection at call time, for event handlers, without subscribing. */
export const useGetSelection = () => useEditorContext().selection.getState

/**
 * Runs `handler` when the editor's keybinding `name` is pressed, anywhere on the page.
 * Except for bindings marked `allowWhileTyping`, it's ignored in text fields, selects and menus.
 * The key's default is prevented unless the handler returns `false` (it doesn't apply now).
 */
// A press that matches two spellings of a binding (⌘= and ⌘equal) runs it once
const handledPresses = new WeakSet<KeyboardEvent>()

type LayoutNavigator = Navigator & { keyboard?: { getLayoutMap?: () => Promise<KeyboardLayout> } }
let layoutRequest: Promise<KeyboardLayout | undefined> | undefined

/** What each key prints on the user's keyboard, where the browser says (Chrome, Edge). */
const useKeyboardLayout = () => {
  const [layout, setLayout] = useState<KeyboardLayout>()
  useEffect(() => {
    let mounted = true
    layoutRequest ??= (navigator as LayoutNavigator).keyboard?.getLayoutMap?.()
      .catch(() => undefined) ?? Promise.resolve(undefined)
    layoutRequest.then((found) => {
      if (mounted) setLayout(found)
    })

    return () => {
      mounted = false
    }
  }, [])

  return layout
}

export const useShortcut = (
  name: string,
  handler: (event: KeyboardEvent) => boolean | void,
) => {
  const binding = useEditorContext().keybindings[name]
  const layout = useKeyboardLayout()
  const handlerRef = useRef(handler)
  handlerRef.current = handler
  const hotkeys = useMemo(() => (binding ? toHotkeys(binding, layout) : []), [binding, layout])
  const characters = new Map(hotkeys.map(({ keys, character }) => [keys, character]))

  useHotkeys(
    hotkeys.map(({ keys }) => keys),
    (event, hotkey) => {
      if (handledPresses.has(event)) return
      if (!typesKey(event, characters.get(hotkey.hotkey), layout)) return
      if (handlerRef.current(event) === false) return

      handledPresses.add(event)
      event.preventDefault()
    },
    {
      enabled: !!binding,
      // What the key types, so ⌘Z is Z on AZERTY and QWERTZ too; see typesKey
      useKey: true,
      enableOnFormTags: binding?.allowWhileTyping ?? false,
      enableOnContentEditable: binding?.allowWhileTyping ?? false,
      ignoreEventWhen: binding?.skipOnControls
        ? (event) => isControlTarget(event.target)
        : undefined,
      // Capture phase: a dialog around the editor can't stop the key on its way (Chakra's
      // modal stops Esc)
      eventListenerOptions: { capture: true },
    },
    [binding, layout],
  )
}

export default Editor
